import { useEffect, useRef } from 'react';
import { Bell, Package, Briefcase, ArrowLeftRight, Settings, Check, CheckCheck } from 'lucide-react';
import { useNotificationStore } from '@/store/notificationStore';
import { useNotifications, useMarkRead, useNotificationSocket } from '@/hooks/useNotification';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';

dayjs.extend(relativeTime);
dayjs.locale('vi');

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
  SYSTEM: {
    icon: <Settings size={16} />,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
  },
  ORDER: {
    icon: <Package size={16} />,
    color: 'text-green-500',
    bg: 'bg-green-50',
  },
  TRANSFER: {
    icon: <ArrowLeftRight size={16} />,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
  },
  JOB: {
    icon: <Briefcase size={16} />,
    color: 'text-purple-500',
    bg: 'bg-purple-50',
  },
};

export default function NotificationPanel() {
  const { notifications, unreadCount, panelOpen, setPanelOpen, markAllAsRead } = useNotificationStore();
  const panelRef = useRef<HTMLDivElement>(null);
  const { isLoading } = useNotifications();
  const markReadMutation = useMarkRead();

  // Lắng nghe notification real-time
  useNotificationSocket();

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    if (panelOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panelOpen, setPanelOpen]);

  const handleMarkRead = (id: string) => {
    markReadMutation.mutate(id);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setPanelOpen(!panelOpen)}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 relative transition-all"
        id="notification-bell"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full text-[9px] font-bold text-white bg-red-500 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {panelOpen && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-h-[520px] rounded-2xl border shadow-2xl z-[200] overflow-hidden animate-slide-down bg-white">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-800">Thông báo</h3>
              {unreadCount > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold text-white bg-orange-500">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors"
              >
                <CheckCheck size={14} />
                Đọc tất cả
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-[420px] divide-y divide-gray-50">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto" />
                <p className="text-xs text-gray-400 mt-3">Đang tải...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-3">
                  <Bell size={28} className="text-gray-300" />
                </div>
                <p className="text-sm font-semibold text-gray-500">Chưa có thông báo</p>
                <p className="text-xs text-gray-400 mt-1">Thông báo mới sẽ hiển thị tại đây</p>
              </div>
            ) : (
              notifications.map((noti) => {
                const config = TYPE_CONFIG[noti.type] || TYPE_CONFIG.SYSTEM;
                return (
                  <button
                    key={noti.id}
                    onClick={() => !noti.isRead && handleMarkRead(noti.id)}
                    className={cn(
                      'w-full flex items-start gap-3 px-5 py-3.5 text-left transition-all hover:bg-orange-50/40',
                      !noti.isRead && 'bg-orange-50/20',
                    )}
                  >
                    {/* Icon */}
                    <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5', config.bg, config.color)}>
                      {config.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm leading-snug', !noti.isRead ? 'font-bold text-gray-800' : 'font-medium text-gray-600')}>
                        {noti.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{noti.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1.5 font-medium">{dayjs(noti.createdAt).fromNow()}</p>
                    </div>

                    {/* Unread dot */}
                    {!noti.isRead && <div className="w-2.5 h-2.5 rounded-full bg-orange-500 shrink-0 mt-2 animate-pulse" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
